import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Chip, cn, IconButton } from '@c2fo/liquidity';
import { PencilIcon } from '@c2fo/liquidity/icons';
import { TakerMarket } from '@/enterprise/data/useTakerMarkets';
import RateDisplay from '@/enterprise/features/homepage/marketTable/RateDisplay';
import { useOfferDiscountAPRDisplay } from '@/enterprise/features/takerMarketTable/utils/useOfferDiscountAPRDisplay';
import { useReporting } from '@/reporting';
import RadioInput from '../../RadioInput';
import { RateType, useOfferBuilderContext } from '../OfferBuilderContext';
import useEnablePricing from '../useEnablePricing';
import { buildTakerMarketKey } from '../utils';
import NameYourRateModal from './NameYourRate/NameYourRateModal';

interface PricingStrategyRadioGroupProps {
  hasError: boolean;
  takerMarket: TakerMarket;
  marketPricingType: TakerMarket['marketPricingType'];
  options: {
    pricingRateUuid: string | undefined;
    rate: number | undefined;
    rateType: RateType;
    suggestRateType: string | undefined;
    suggestedRate: number | undefined;
    suggestedRateEnd: number | undefined;
    suggestedRateStart: number | undefined;
    type: string;
  }[];
  selectedRateType: RateType;
}

const NameYourRateOption = ({
  takerMarket,
  rateTypeLabel,
  option,
  radioGroupName,
  selectedRateType,
  hasError,
}: {
  takerMarket: TakerMarket;
  rateTypeLabel: string;
  option?: PricingStrategyRadioGroupProps['options'][number];
  radioGroupName: string;
  selectedRateType: RateType;
  hasError: boolean;
}) => {
  const { t } = useTranslation();
  const {
    dispatch,
    state: { workingTakerMarkets },
  } = useOfferBuilderContext();
  const [open, setOpen] = useState(false);
  const { trackEnterpriseEvent } = useReporting();
  const { offerDiscountAPRDisplay } = useOfferDiscountAPRDisplay();

  const previousRate = takerMarket.offerConfig.maxApr ?? takerMarket.offerConfig.maxDiscount;
  const previousRateType = takerMarket.offerConfig.isDiscountBidding ? 'DISC' : 'APR';
  const hasPreviousRate = previousRate !== undefined && previousRate !== null && previousRateType === selectedRateType;
  const marketKey = buildTakerMarketKey(takerMarket);
  const marketRate = workingTakerMarkets[marketKey];
  const showEditRateButton = hasPreviousRate || marketRate.customRate;

  const onChange = () => {
    // if custom rate already exists, use it
    if (marketRate.customRate && marketRate.customRateType) {
      return dispatch({
        type: 'UPDATE_WORKING_TAKER_MARKETS_RATE',
        payload: [
          {
            takerMarketKey: marketKey,
            rateInfo: {
              rate: marketRate.customRate,
              rateType: marketRate.customRateType,
              pricingRateUuid: marketRate.pricingRateUuid,
              suggestedRate: marketRate.suggestedRate,
              suggestRateType: marketRate.suggestRateType,
              customRate: marketRate.customRate,
              customRateType: marketRate.customRateType,
              pricingStrategy: 'nameYourRate',
              isIndividuallyOverridden: true,
            },
          },
        ],
      });
    }

    // if taker market has a previous rate, use it
    if (hasPreviousRate) {
      return dispatch({
        type: 'UPDATE_WORKING_TAKER_MARKETS_RATE',
        payload: [
          {
            takerMarketKey: marketKey,
            rateInfo: {
              rate: previousRate,
              rateType: previousRateType,
              pricingRateUuid: marketRate.pricingRateUuid,
              suggestedRate: marketRate.suggestedRate,
              suggestRateType: marketRate.suggestRateType,
              customRate: previousRate,
              customRateType: previousRateType,
              pricingStrategy: 'nameYourRate',
              isIndividuallyOverridden: true,
            },
          },
        ],
      });
    }

    // if no custom rate exists, open modal
    setOpen(true);
    trackEnterpriseEvent('offer-builder::name-your-rate-modal::opened', {
      type: 'enter-rate',
    });
  };

  const isCustomRateSelected = marketRate.pricingStrategy === 'nameYourRate';

  return (
    <>
      <NameYourRateModal
        takerMarket={takerMarket}
        option={option}
        open={open}
        onClose={() => {
          setOpen(false);
          trackEnterpriseEvent('offer-builder::name-your-rate-modal::closed');
        }}
      />
      <RadioInput
        id={`${takerMarket.id}-nameYourRate`}
        checked={!!isCustomRateSelected}
        className={cn({ 'border-red-600': hasError })}
        color="blue"
        name={radioGroupName}
        endSlot={
          <div>
            <Chip label={t('core.dependsOnOffer')} size="sm" type="default" variant="outlined" />
          </div>
        }
        label={
          <div className="flex items-center gap-2">
            <div>
              <div className="font-bold">
                {marketRate.customRate ? (
                  <span className="uppercase">{offerDiscountAPRDisplay(marketRate.customRate, rateTypeLabel)}</span>
                ) : hasPreviousRate ? (
                  <span className="uppercase">{offerDiscountAPRDisplay(previousRate, previousRateType)}</span>
                ) : (
                  t('core.setARate')
                )}
              </div>
              <div className="text-sm font-normal text-text-secondary">{t('core.nameYourRate')}</div>
            </div>
            {showEditRateButton && (
              <IconButton
                className="bg-transparent hover:bg-black/10"
                icon={PencilIcon}
                name="Edit rate"
                onClick={() => {
                  setOpen(true);
                  trackEnterpriseEvent('offer-builder::name-your-rate-modal::opened', {
                    type: 'update-rate',
                  });
                }}
                size="sm"
                variant="ancillary"
              />
            )}
          </div>
        }
        onChange={onChange}
        size="large"
        value="nameYourRate"
        variant="light"
      />
    </>
  );
};

const SuggestedRateOption = ({
  takerMarket,
  option,
  rateTypeLabel,
  radioGroupName,
  hasError,
}: {
  takerMarket: TakerMarket;
  option: PricingStrategyRadioGroupProps['options'][number];
  rateTypeLabel: string;
  radioGroupName: string;
  hasError: boolean;
}) => {
  const { t } = useTranslation();
  const {
    dispatch,
    state: { workingTakerMarkets, frequency },
  } = useOfferBuilderContext();
  const { trackEnterpriseEvent } = useReporting();
  const { offerDiscountAPRDisplay } = useOfferDiscountAPRDisplay();

  const marketKey = buildTakerMarketKey(takerMarket);
  const marketRate = workingTakerMarkets[marketKey];

  const onChange = () => {
    if (!option.suggestedRate) {
      return;
    }

    trackEnterpriseEvent('offer-builder::rate::set', {
      rate: option.suggestedRate,
      rateType: option.rateType,
      pricingStrategy: option.type,
      frequency,
      marketUuid: takerMarket.marketUuid,
      takerDivisionUuid: takerMarket.takerDivisionUuid,
      makerOrganizationUuid: takerMarket.makerOrganizationUuid,
    });

    dispatch({
      type: 'UPDATE_WORKING_TAKER_MARKETS_RATE',
      payload: [
        {
          takerMarketKey: marketKey,
          rateInfo: {
            rate: option.suggestedRate,
            rateType: option.rateType,
            pricingRateUuid: option.pricingRateUuid,
            suggestedRate: option.suggestedRate,
            suggestRateType: option.rateType,
            customRate: marketRate.customRate,
            customRateType: marketRate.customRateType,
            pricingStrategy: option.type as 'trendingRate' | 'expressAccept',
            isIndividuallyOverridden: true,
          },
        },
      ],
    });

    dispatch({
      type: 'SET_RATE_TYPE',
      payload: option.rateType,
    });
  };

  // each market can only have one active suggested-rate strategy; verify the selected
  // rate still matches the current pricing data to detect stale selections after a refresh.
  // UUID comparison is preferred; rate+rateType is the fallback when either UUID is absent.
  const isMatchingSuggestedRate =
    marketRate != null &&
    ((marketRate.pricingRateUuid != null &&
      option.pricingRateUuid != null &&
      marketRate.pricingRateUuid === option.pricingRateUuid) ||
      (marketRate.rate === option.suggestedRate && marketRate.rateType === option.rateType));
  const isSuggestedRateSelected = marketRate?.pricingStrategy === option.type && isMatchingSuggestedRate;

  // when pricing data refreshes and this option's rate no longer matches what was stored,
  // clear the stale rate so validation correctly blocks proceeding until the user re-selects
  const isStale = marketRate?.pricingStrategy === option.type && !isMatchingSuggestedRate;

  useEffect(() => {
    if (isStale) {
      dispatch({
        type: 'UPDATE_WORKING_TAKER_MARKETS_RATE',
        payload: [{ takerMarketKey: marketKey, rateInfo: undefined }],
      });
    }
  }, [isStale, dispatch, marketKey]);

  const pricingTypeLabelMap = {
    expressAccept: t('core.expressAccept'),
    trendingRate: t('core.trending'),
  };

  return (
    <RadioInput
      id={`${takerMarket.id}-${option.type}`}
      checked={isSuggestedRateSelected}
      className={cn({ 'border-red-600': hasError })}
      color="blue"
      name={radioGroupName}
      endSlot={
        <div>
          {option.type === 'expressAccept' ? (
            <Chip label={t('core.instantSuccess')} size="sm" type="primary" variant="outlined" />
          ) : (
            <Chip label={t('core.aboveAverageSuccess')} size="sm" type="info" variant="outlined" />
          )}
        </div>
      }
      label={
        <div>
          <div className="font-bold uppercase">{offerDiscountAPRDisplay(option.suggestedRate!, rateTypeLabel)}</div>
          <div className="text-sm font-normal text-text-secondary">
            {pricingTypeLabelMap[option.type as keyof typeof pricingTypeLabelMap]}
          </div>
        </div>
      }
      onChange={onChange}
      size="large"
      value={option.type}
      variant="light"
    />
  );
};

const PricingStrategyRadioGroup = ({
  hasError,
  takerMarket,
  marketPricingType,
  options,
  selectedRateType,
}: PricingStrategyRadioGroupProps) => {
  const { t } = useTranslation();
  const enablePricing = useEnablePricing();
  const rateTypeLabel = selectedRateType === 'APR' ? t('core.apr') : t('core.discountAbbreviation');

  if (marketPricingType === 'PRICE_DISCOVERY') {
    const radioGroupName = `pricing-strategy-${takerMarket.id}`;

    return (
      <>
        <div className={cn('pb-4 text-sm text-text-secondary', { 'text-red-600': hasError })}>
          {t('builder.selectYourRate')}
        </div>
        <fieldset>
          <legend className="sr-only">{t('core.pricingStrategy')}</legend>
          <div className="space-y-2">
            {enablePricing &&
              options.map((option) => (
                <SuggestedRateOption
                  key={option.type}
                  takerMarket={takerMarket}
                  option={option}
                  rateTypeLabel={rateTypeLabel}
                  radioGroupName={radioGroupName}
                  hasError={hasError}
                />
              ))}
            <NameYourRateOption
              takerMarket={takerMarket}
              option={enablePricing ? options.find((option) => option.type === 'trendingRate') : undefined}
              rateTypeLabel={rateTypeLabel}
              radioGroupName={radioGroupName}
              selectedRateType={selectedRateType}
              hasError={hasError}
            />
          </div>
        </fieldset>
      </>
    );
  }

  if (marketPricingType === 'BENCHMARK' || marketPricingType === 'STATIC') {
    return (
      <RadioInput
        checked
        color="blue"
        label={
          <div>
            <div className="font-bold uppercase">
              <RateDisplay showDiscountAmount={false} takerMarket={takerMarket} />
            </div>
            <div className="text-sm font-normal text-text-secondary">
              {marketPricingType === 'BENCHMARK' ? t('core.variableRate') : t('core.fixedRate')}
            </div>
          </div>
        }
        onChange={() => {
          //no op
        }}
        size="large"
        value=""
        variant="light"
      />
    );
  }

  return null;
};

export default PricingStrategyRadioGroup;
